import React, { useEffect, useState } from 'react';
import { PortfolioItem } from '@/hooks/useProfile';
import GalleryItem from './portfolio/GalleryItem';
import { useIsMobile } from '@/hooks/use-mobile';
import PortfolioItemModal from './PortfolioItemModal';
import MobilePortfolioModal from './portfolio/modal/MobilePortfolioModal';
import { supabase } from '@/integrations/supabase/client';
// Remove: import { useProfileContext } from '@/contexts/ProfileContext';

function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

function getPortfolioSlug(item: PortfolioItem) {
  return item.slug || slugify(item.title || item.id);
}

function findItemBySlug(items: PortfolioItem[], slug: string) {
  return items.find(item => getPortfolioSlug(item) === slug);
}

// Helper for rate limiting: only one view per item per session per hour
function canRecordPortfolioView(itemId: string): boolean {
  const key = `portfolio-view-${itemId}`;
  const lastView = localStorage.getItem(key);
  const now = Date.now();
  if (lastView && now - parseInt(lastView) < 60 * 60 * 1000) {
    return false;
  }
  localStorage.setItem(key, now.toString());
  return true;
}

async function recordPortfolioItemView(profileId: string, itemId: string, viewerId: string | null = null) {
  try {
    await supabase.from('portfolio_item_views' as any).insert([
      {
        profile_id: profileId,
        portfolio_item_id: itemId,
        viewer_id: viewerId,
      },
    ]);
  } catch (error) {
    console.error('Error recording portfolio item view:', error);
  }
}

interface PortfolioGalleryProps {
  username: string;
  username_slug: string;
  portfolioItems: PortfolioItem[];
  profile: { 
    id: string;
    avatar_url?: string | null;
    profession?: string | null;
    display_name?: string | null;
  };
  imageLoadStatus?: Record<string, 'loading' | 'success' | 'error'>;
  openPortfolioModal?: PortfolioItem | null;
  onClosePortfolioModal?: () => void;
}

const PortfolioGallery = ({
  username,
  username_slug,
  portfolioItems,
  profile,
  imageLoadStatus = {},
  openPortfolioModal,
  onClosePortfolioModal,
}: PortfolioGalleryProps) => {
  // Modal state (local unless controlled from above)
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  // Remove: const { profile } = useProfileContext ? useProfileContext() : { profile: null };

  // Open modal and update SEO-friendly URL
  const openModal = (item: PortfolioItem) => {
    setSelectedItem(item);
    const slug = getPortfolioSlug(item);
    window.history.pushState({}, '', `/u/${username_slug}/portfolio/${slug}`);
    // Record a view (rate limited)
    if (profile && canRecordPortfolioView(item.id)) {
      recordPortfolioItemView(profile.id, item.id, null); // Pass viewerId if available
    }
  };

  // Close modal and revert to profile URL
  const closeModal = () => {
    setSelectedItem(null);
    window.history.replaceState({}, '', `/u/${username_slug}`);
  };

  // On mount, check for SEO-friendly modal URL and open modal if present
  useEffect(() => {
    if (openPortfolioModal) return; // Don't auto-open if controlled from above
    const match = window.location.pathname.match(
      /^\/u\/([^/]+)\/portfolio\/([^/]+)/
    );
    if (match) {
      const slug = match[2];
      const item = findItemBySlug(portfolioItems, slug);
      if (item) setSelectedItem(item);
    }
    // Listen for popstate to close modal if user presses back
    const onPopState = () => {
      const match = window.location.pathname.match(
        /^\/u\/([^/]+)\/portfolio\/([^/]+)/
      );
      if (!match) {
        setSelectedItem(null);
      } else {
        const slug = match[2];
        const item = findItemBySlug(portfolioItems, slug);
        if (item) setSelectedItem(item);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [portfolioItems, username_slug, openPortfolioModal]);

  // Sort portfolio items: featured item first, then by user-defined order (order property), then by created_at
  const sortedPortfolioItems = React.useMemo(() => {
    if (!portfolioItems || portfolioItems.length === 0) return [];

    // Separate featured and non-featured
    const featured = portfolioItems.find(item => item.is_featured);
    const nonFeatured = portfolioItems.filter(item => !item.is_featured);

    // Sort non-featured by 'order' if present, else by created_at
    const sortedNonFeatured = [...nonFeatured].sort((a, b) => {
      if (typeof a.order === 'number' && typeof b.order === 'number') {
        return a.order - b.order;
      }
      // fallback to created_at descending
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    if (featured) {
      return [featured, ...sortedNonFeatured];
    }
    return sortedNonFeatured;
  }, [portfolioItems]);

  // Generate aspect ratio classes based on position in the grid
  const getAspectRatioClass = (item: PortfolioItem, index: number): string => {
    if (window.innerWidth < 768) {
      return 'aspect-[16/9]';
    }
    if (index === 0 && item.is_featured) {
      return 'aspect-[16/9]';
    } else {
      return 'aspect-[4/3]';
    }
  };

  const getYearFromItem = (item: PortfolioItem): string => {
    if (item.created_at) {
      return new Date(item.created_at).getFullYear().toString();
    }
    if (item.updated_at) {
      return new Date(item.updated_at).getFullYear().toString();
    }
    return new Date().getFullYear().toString();
  };

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use controlled modal if provided, else local state
  const modalItem = openPortfolioModal !== undefined ? openPortfolioModal : selectedItem;
  const handleClose = onClosePortfolioModal || closeModal;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {sortedPortfolioItems.map((item, index) => {
          const itemId = item.id || item.title;
        const imageUrl = item.images && item.images.length > 0 ? item.images[0].url : item.image_url;
        const imageCount = item.images ? item.images.length : 1;
          const hasMultipleImages = imageCount > 1;
          const yearText = getYearFromItem(item);
          const aspectRatioClass = isMobile ? 'aspect-[16/9]' : getAspectRatioClass(item, index);
          const gridClass = !isMobile && index === 0 && item.is_featured ? 'md:col-span-2' : '';
          return (
            <div key={itemId} className={`${gridClass}`}>
              <GalleryItem
                item={item}
                itemId={itemId}
                imageUrl={imageUrl}
                hasMultipleImages={hasMultipleImages}
                imageCount={imageCount}
                imageLoadStatus={imageLoadStatus}
                aspectRatioClass={aspectRatioClass}
                yearText={yearText}
                usernameSlug={username_slug}
                onOpenModal={openModal}
              />
            </div>
          );
        })}
      </div>
      {modalItem && (
        isMobile ? (
          <MobilePortfolioModal
            item={modalItem}
            isOpen={!!modalItem}
            onClose={handleClose}
            username={username}
            username_slug={username_slug}
            profile={profile}
          />
        ) : (
          <PortfolioItemModal
            item={modalItem}
            isOpen={!!modalItem}
            onClose={handleClose}
            username={username}
            username_slug={username_slug}
          />
        )
      )}
    </>
  );
};

export default PortfolioGallery;
